(function () {
    'use strict';
    
    // Shared helper to generate unique ID and ensure element has one
    const ensureElementId = (el, prefix) => {
        if (!el.id) {
            el.id = `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
        }
        return el.id;
    };
    
    // Shared helper to update button state
    const updateButtonState = (btn, expanded, expandedLabel, collapsedLabel) => {
        btn.textContent = expanded ? expandedLabel : collapsedLabel;
        btn.setAttribute('aria-expanded', expanded.toString());
        btn.setAttribute('aria-label', expanded ? expandedLabel : collapsedLabel);
    };
    
    const initClamp = (el) => {
        
        // Check if this is a list element itself
        if (el.matches('ul, ol')) {
            if (el.hasAttribute('data-clamp-paging')) {
                initListPaging(el);
            } else {
                initListClamp(el);
            }
        } else {
            initTextClamp(el);
        }
    };

    const getListItems = (list) => Array.from(list.children).filter(child => 
        child.tagName === 'LI'
    );

    const createClampAction = (list, className, listId) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = className;
        btn.setAttribute('aria-controls', listId);

        const btnWrap = document.createElement("p");
        btnWrap.className = "qld__clamp__actions";
        btnWrap.appendChild(btn);

        const insertAfter = list.parentElement?.classList.contains('row')
            ? list.parentElement
            : list;
        insertAfter.after(btnWrap);

        return { btn, btnWrap };
    };

    const initListClamp = (list) => {
        const moreLabel = list.dataset.moreLabel || "Show all";
        const lessLabel = list.dataset.lessLabel || "Show less";
        const maxItems = parseInt(list.dataset.clampItems || '3', 10);
        const listId = ensureElementId(list, 'clamp-list');

        let btn = null;
        let btnWrap = null;
        let expanded = false;

        const measure = () => {
            const items = getListItems(list);

            if (items.length <= maxItems) {
                items.forEach(item => item.classList.remove('hidden'));
                if (btnWrap) btnWrap.style.display = "none";
                return;
            }

            items.forEach((item, index) => {
                if (expanded || index < maxItems) {
                    item.classList.remove('hidden');
                } else {
                    item.classList.add('hidden');
                }
            });

            if (!btn) {
                ({ btn, btnWrap } = createClampAction(list, "qld__btn qld__btn--tertiary qld__no-print", listId));
                btn.addEventListener("click", () => {
                    expanded = !expanded;
                    measure();
                });
            } else {
                btnWrap.style.display = "";
            }

            updateButtonState(btn, expanded, lessLabel, moreLabel);
        };

        measure();
        new MutationObserver(measure).observe(list, { childList: true });
    };

    const initListPaging = (list) => {
        const moreLabel = list.dataset.moreLabel || "Show more";
        const maxItems = parseInt(list.dataset.clampItems || '3', 10);
        const listId = ensureElementId(list, 'clamp-list');

        let btn = null;
        let btnWrap = null;
        let shown = maxItems;

        const measure = () => {
            const items = getListItems(list);

            if (items.length <= maxItems) {
                items.forEach(item => item.classList.remove('hidden'));
                if (btnWrap) btnWrap.style.display = "none";
                return;
            }

            items.forEach((item, index) => {
                item.classList.toggle('hidden', index >= shown);
            });

            if (shown >= items.length) {
                if (btnWrap) btnWrap.style.display = "none";
                return;
            }

            if (!btn) {
                ({ btn, btnWrap } = createClampAction(list, "qld__btn qld__btn--secondary qld__no-print", listId));
                btn.setAttribute('aria-expanded', 'false');
                btn.addEventListener("click", () => {
                    shown = Math.min(shown + maxItems, getListItems(list).length);
                    measure();
                });
            } else {
                btnWrap.style.display = "";
            }

            btn.textContent = moreLabel;
            btn.setAttribute('aria-label', moreLabel);
        };

        measure();
        new MutationObserver(measure).observe(list, { childList: true });
    };

    const initTextClamp = (el) => {
        el.classList.add("qld__clamped");

        const moreLabel = el.dataset.moreLabel || "Read more";
        const lessLabel = el.dataset.lessLabel || "Read less";

        let btn = null;
        let btnWrap = null;
        let rafId = 0;
        
        const elId = ensureElementId(el, 'clamp-text');

        const measure = () => {
            const clamped = Math.ceil(el.clientHeight);
            const full = Math.ceil(el.scrollHeight);
            const needsClamp = full > clamped + 1;

            if (!needsClamp) {
                el.classList.remove("qld__clamped");
                if (btnWrap) btnWrap.style.display = "none";
                return;
            }

            if (!btn) {
                btn = document.createElement("button");
                btn.type = "button";
                btn.className = "qld__btn qld__btn--secondary qld__no-print";
                btn.setAttribute('aria-controls', elId);

                btnWrap = document.createElement("p");
                btnWrap.className = "qld__clamp__actions";
                btnWrap.appendChild(btn);

                el.after(btnWrap);

                btn.addEventListener("click", () => {
                    const nowClamped = el.classList.toggle("qld__clamped");
                    updateButtonState(btn, !nowClamped, lessLabel, moreLabel);
                });
            } else {
                if (btnWrap) btnWrap.style.display = "";
            }

            const isClamped = el.classList.contains("qld__clamped");
            updateButtonState(btn, !isClamped, lessLabel, moreLabel);
        };

        const scheduleMeasure = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(measure);
        };

        const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
        ready.then(() => requestAnimationFrame(measure));

        const ro = new ResizeObserver(scheduleMeasure);
        ro.observe(el);

        const mo = new MutationObserver(scheduleMeasure);
        mo.observe(el, { childList: true, subtree: true, characterData: true });
    };

    /**
     * @module clamp
     */
    var clamp = {};
    
    /**
     * Initialize clamp on all elements with [data-clamp] attribute
     * @param {HTMLElement|string} container - Optional container to search within
     */
    clamp.init = function (container) {
        const root =
            container instanceof Element || container instanceof Document
                ? container
                : typeof container === "string"
                    ? document.querySelector(container)
                    : document;

        root.querySelectorAll("[data-clamp]").forEach(initClamp);
    };

    
    /**
     * Initialize clamp on a specific element
     * @param {HTMLElement|string} element - Element or selector to initialize clamp on
     */
    clamp.initElement = function (element) {
        if (typeof element === 'string') {
            element = document.querySelector(element);
        }
        if (element && element.nodeType === 1) {
            initClamp(element);
        }
    };

    // Expose to global QLD object
    window.QLD = window.QLD || {};
    QLD.clamp = clamp;

    // Auto-initialize on DOMContentLoaded
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => clamp.init(), { once: true });
    } else {
        clamp.init();
    }
}());




